import { useState } from "react";

import {
  GOOGLE_METHOD,
  OTP_METHOD,
  PASSWORD_METHOD,
  useLoginBranding,
} from "../branding/loginBranding";

import { LoginAuthCard } from "./LoginAuthCard";
import { LoginBrandingPanel } from "./LoginBrandingPanel";
import { LoginMethodTabs, type LoginMethod } from "./LoginMethodTabs";
import { LoginPasswordCard } from "./LoginPasswordCard";
import type { LoginController } from "./useLoginController";
import { useLoginViewState } from "./useLoginViewState";
import "./login.css";

export type LoginViewProps = Pick<
  LoginController,
  | "loading"
  | "step"
  | "phone"
  | "otp"
  | "country"
  | "setCountry"
  | "localError"
  | "resendTimer"
  | "mockOtp"
  | "loginLogoUrl"
  | "otpSentText"
  | "selectionSummary"
  | "showTenantSelection"
  | "showContextSelection"
  | "tenantOptions"
  | "contextOptions"
  | "selectedTenantId"
  | "selectedContextKey"
  | "phoneRef"
  | "otpRef"
  | "setPhoneFromInput"
  | "setOtpFromInput"
  | "setSelectedTenantId"
  | "setSelectedContextKey"
  | "handleSendOtp"
  | "handleVerifyOtp"
  | "handleActivateSession"
  | "handleResend"
  | "handleChangeNumber"
  | "onPhoneKeyDown"
  | "onOtpKeyDown"
> & {
  formattedPhone: string;
};

export function LoginView(props: LoginViewProps) {
  const {
    logoSrc,
    logoAlt,
    schoolName,
    heroImageSrc,
    authMethods,
    loginTitle,
    loginSubtitle,
  } = useLoginBranding();
  const availableMethods = ([OTP_METHOD, PASSWORD_METHOD] as LoginMethod[]).filter(
    (method) => authMethods.includes(method),
  );
  const [activeMethod, setActiveMethod] = useState<LoginMethod | null>(null);
  const selectedMethod = activeMethod ?? availableMethods[0] ?? null;
  const canSignIn = availableMethods.length > 0;

  // Google is a button rather than a tab: it replaces the credential form entirely instead of being
  // an alternative set of fields, so it sits alongside whichever form is showing.
  const googleEnabled = authMethods.includes(GOOGLE_METHOD);
  const viewState = useLoginViewState({
    step: props.step,
    loading: props.loading,
    localError: props.localError,
    resendTimer: props.resendTimer,
    handleSendOtp: props.handleSendOtp,
    handleVerifyOtp: props.handleVerifyOtp,
    handleActivateSession: props.handleActivateSession,
    handleChangeNumber: props.handleChangeNumber,
    onPhoneKeyDown: props.onPhoneKeyDown,
    onOtpKeyDown: props.onOtpKeyDown,
  });

  return (
    <main className="login-page">
      <div className="login-layout">
        <LoginBrandingPanel
          logoSrc={logoSrc}
          logoAlt={logoAlt}
          schoolName={schoolName}
          heroImageSrc={heroImageSrc}
        />
        {!canSignIn ? (
          <section className="login-auth" aria-labelledby="login-title">
            <div className="login-card">
              <h2 id="login-title">Sign-in unavailable</h2>
              <p className="login-description">
                {schoolName} has not enabled a sign-in method that this app can use yet.
                Please contact your school administrator.
              </p>
            </div>
          </section>
        ) : selectedMethod === PASSWORD_METHOD ? (
          <LoginPasswordCard
            methods={availableMethods}
            activeMethod={selectedMethod}
            onSelectMethod={setActiveMethod}
            googleEnabled={googleEnabled}
            loginTitle={loginTitle}
            loginSubtitle={loginSubtitle}
          />
        ) : (
          <LoginAuthCard
            methodTabs={
              <LoginMethodTabs
                methods={availableMethods}
                active={OTP_METHOD}
                onSelect={setActiveMethod}
              />
            }
            googleEnabled={googleEnabled}
            country={props.country}
            setCountry={props.setCountry}
            loginTitle={loginTitle}
            loginSubtitle={loginSubtitle}
            step={props.step}
            formattedPhone={props.formattedPhone}
            otp={props.otp}
            localError={props.localError}
            resendTimer={props.resendTimer}
            mockOtp={props.mockOtp}
            otpSentText={props.otpSentText}
            selectionSummary={props.selectionSummary}
            showTenantSelection={props.showTenantSelection}
            showContextSelection={props.showContextSelection}
            tenantOptions={props.tenantOptions}
            contextOptions={props.contextOptions}
            selectedTenantId={props.selectedTenantId}
            selectedContextKey={props.selectedContextKey}
            phoneRef={props.phoneRef}
            otpRef={props.otpRef}
            setPhoneFromInput={props.setPhoneFromInput}
            setOtpFromInput={props.setOtpFromInput}
            setSelectedTenantId={props.setSelectedTenantId}
            setSelectedContextKey={props.setSelectedContextKey}
            handleResend={props.handleResend}
            loading={props.loading}
            {...viewState}
          />
        )}
      </div>
    </main>
  );
}
